import { API_BASE_URL } from '@synergy/config';
import { AlertCallSignIcon, NoEuroIcon } from '@synergy/theme-next';
import { BonusSettlementCustomerStatus, getBonusSettlementCustomerTranslationId } from '@synergy/models';
import { Box, Stack, Tooltip } from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { PaloAmount, PaloColumn, PaloListView, PaloRowAction, PaloStatusPill } from '@synergy/components-palo';
import { PaloNextThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolConfirmationModal, PoolDocumentTitle, PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { padZeroLeft } from '@synergy/utils';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { BonusSettlementCustomer } from '../../models/bonus-settlement-customer-overview';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementsCustomerOverviewRequestingSelector,
    bonusSettlementsCustomerOverviewSelector,
} from '../store/bonus-settlements-customer-overview-selector';
import {
    completeCustomers,
    fetchBonusSettlementsCustomerOverview,
    fetchBonusSettlementsCustomerOverviewDetails,
    reloadCustomer,
    reopenCustomer,
} from '../store/bonus-settlements-customer-overview-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { usePaloListViews } from '../../../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const COMPLETE_TEXT = 'app.bonusSettlements.customerOverview.action.completeCustomer.text';

export const PaloBonusSettlementCustomerList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns } =
        usePaloListViews('bonus-settlement-customers');
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const { id: bonusSettlementId } = useParams<{ id: string }>();

    const customers = useAppSelector(bonusSettlementsCustomerOverviewSelector);
    const requesting = useAppSelector(bonusSettlementsCustomerOverviewRequestingSelector);

    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get('page') ?? 1);
    const limit = Number(searchParams.get('limit') ?? 50);

    const [completeCustomer, setCompleteCustomer] = useState<BonusSettlementCustomer | null>(null);
    const [reopenCustomerRow, setReopenCustomer] = useState<BonusSettlementCustomer | null>(null);

    useEffect(() => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(fetchBonusSettlementsCustomerOverview({ id: +bonusSettlementId, searchParams }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, searchParams]);

    // The totals belong to the settlement, not to the page of it on screen, so
    // they are fetched once rather than with every filter and every page turn.
    useEffect(() => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(fetchBonusSettlementsCustomerOverviewDetails(+bonusSettlementId));
        }
    }, [dispatch, bonusSettlementId]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const onExportCustomer = (customerId: number): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            window.open(
                urlJoin(API_BASE_URL, `/bonus-settlements/${bonusSettlementId}/customers/${customerId}/export`),
                '_self'
            );
        }
    };

    const openDetail = (customer: BonusSettlementCustomer): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            navigate(
                ROUTES.bonusSettlements.customerDetail
                    .replace(':bonusSettlementId', bonusSettlementId)
                    .replace(':id', customer.customerId.toString())
            );
        }
    };

    const onConfirmCompleteCustomer = async (customer: BonusSettlementCustomer): Promise<void> => {
        if (bonusSettlementId && +bonusSettlementId) {
            await dispatch(
                completeCustomers({
                    bonusSettlementId: +bonusSettlementId,
                    completeAll: false,
                    customerIds: [customer.customerId],
                })
            );
            dispatch(fetchBonusSettlementsCustomerOverview({ id: +bonusSettlementId, searchParams }));
            dispatch(fetchBonusSettlementsCustomerOverviewDetails(+bonusSettlementId));
        }
        setCompleteCustomer(null);
    };

    const onReloadCustomer = (customer: BonusSettlementCustomer): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(reloadCustomer({ bonusSettlementId: +bonusSettlementId, customerId: customer.customerId }));
        }
    };

    const onReopenCustomer = async (customer: BonusSettlementCustomer): Promise<void> => {
        if (bonusSettlementId && +bonusSettlementId) {
            await dispatch(reopenCustomer({ bonusSettlementId: +bonusSettlementId, customerId: customer.customerId }));
            dispatch(fetchBonusSettlementsCustomerOverviewDetails(+bonusSettlementId));
        }
        setReopenCustomer(null);
    };

    /**
     * What completing a member sets off: a member completed before was told
     * already, one without a bonus is not told at all (R-168, R-170).
     */
    const completeTextOf = (customer: BonusSettlementCustomer): string => {
        if (customer.documents.length) {
            return COMPLETE_TEXT + 'Again';
        }

        return 0 === Math.round((customer.memberBonus ?? NaN) * 100) ? COMPLETE_TEXT + 'WithoutBonus' : COMPLETE_TEXT;
    };

    /** The documents reopening leaves standing, as one line each. */
    const documentLines = (customer: BonusSettlementCustomer): string =>
        customer.documents
            .map(document =>
                intl.formatMessage(
                    { id: 'app.bonusSettlements.customerOverview.reopen.document' },
                    {
                        isCredit: String(document.isCredit),
                        number:
                            document.number ??
                            intl.formatMessage({ id: 'app.bonusSettlements.customerOverview.reopen.draft' }),
                        amount: intl.formatNumber(document.totalPrice, { style: 'currency', currency: 'EUR' }),
                    }
                )
            )
            .join(', ');

    // Number, status, member: the order the review asked for. The city is left
    // out — it tells nothing about the settlement (#2193).
    const columns: PaloColumn<BonusSettlementCustomer>[] = [
        {
            key: 'customerId',
            head: <FormattedMessage id="app.table.column.number" />,
            variant: 'ref',
            // Printed the way the member list prints it, so the two can be matched.
            cell: customer => padZeroLeft(customer.customerId, 5),
        },
        {
            key: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: customer => (
                <Stack direction="row" alignItems="center" gap={1}>
                    <PaloStatusPill
                        status={customer.status}
                        label={<FormattedMessage id={getBonusSettlementCustomerTranslationId(customer.status)} />}
                    />
                    {customer.isReclaimed && (
                        <Tooltip title={<FormattedMessage id="app.bonusSettlement.reclaim.customer.toolTip" />}>
                            <Box sx={{ display: 'flex' }}>
                                <AlertCallSignIcon color="error" />
                            </Box>
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
        {
            key: 'customer',
            head: <FormattedMessage id="app.table.column.member" />,
            cell: customer => (
                <Stack direction="row" alignItems="center" gap={1}>
                    {!customer.hasBonusPayout && (
                        <Tooltip
                            title={
                                <FormattedMessage id="app.bonusSettlements.customerOverview.tooltip.hasNoBonusPayout" />
                            }
                            placement="top"
                        >
                            <Box sx={{ display: 'flex' }}>
                                <NoEuroIcon color="primary" />
                            </Box>
                        </Tooltip>
                    )}
                    {customer.customerName}
                </Stack>
            ),
        },
        {
            // The member's revenue with the settlement's suppliers, summed as the
            // member evaluation export sums it.
            key: 'memberRevenue',
            type: 'amount',
            currency: 'EUR',
            head: <FormattedMessage id="app.bonusSettlements.customerOverview.memberRevenue" />,
            cell: customer =>
                null === customer.memberRevenue || undefined === customer.memberRevenue ? (
                    <Tooltip
                        title={<FormattedMessage id="app.bonusSettlements.customerOverview.memberBonus.pending" />}
                    >
                        <Box component="span" sx={{ color: tokens.color.ink3 }}>
                            –
                        </Box>
                    </Tooltip>
                ) : (
                    <PaloAmount value={customer.memberRevenue} />
                ),
        },
        {
            // What the member is paid out, as the credit note books it. Until the
            // member is completed it can still move, and a member without a payout
            // gets no credit note at all — the figure must not suggest otherwise.
            key: 'memberBonus',
            type: 'amount',
            currency: 'EUR',
            head: <FormattedMessage id="app.bonusSettlements.customerOverview.memberBonus" />,
            cell: customer => {
                if (!customer.hasBonusPayout) {
                    return (
                        <Tooltip
                            title={
                                <FormattedMessage id="app.bonusSettlements.customerOverview.tooltip.hasNoBonusPayout" />
                            }
                        >
                            <Box component="span" sx={{ color: tokens.color.ink3 }}>
                                –
                            </Box>
                        </Tooltip>
                    );
                }

                if (null === customer.memberBonus || undefined === customer.memberBonus) {
                    return (
                        <Tooltip
                            title={<FormattedMessage id="app.bonusSettlements.customerOverview.memberBonus.pending" />}
                        >
                            <Box component="span" sx={{ color: tokens.color.ink3 }}>
                                –
                            </Box>
                        </Tooltip>
                    );
                }

                const amount = <PaloAmount value={customer.memberBonus} />;

                if (customer.status === BonusSettlementCustomerStatus.Completed) {
                    return amount;
                }

                return (
                    <Tooltip
                        title={
                            <FormattedMessage id="app.bonusSettlements.customerOverview.memberBonus.provisionalHint" />
                        }
                    >
                        <Box component="span" sx={{ color: tokens.color.ink3 }}>
                            {amount}
                            <Box component="span" sx={{ fontSize: 11, ml: 0.75 }}>
                                <FormattedMessage id="app.bonusSettlements.customerOverview.memberBonus.provisional" />
                            </Box>
                        </Box>
                    </Tooltip>
                );
            },
        },
    ];

    const rowActions = (customer: BonusSettlementCustomer): PaloRowAction[] => {
        const actions: PaloRowAction[] = [
            {
                label: <FormattedMessage id="app.bonusSettlements.customerOverview.action.exportCustomer" />,
                onClick: () => onExportCustomer(customer.customerId),
            },
        ];

        if (customer.status === BonusSettlementCustomerStatus.ReadyToComplete) {
            actions.push(
                {
                    label: <FormattedMessage id="app.bonusSettlements.customerOverview.action.complete" />,
                    onClick: () => setCompleteCustomer(customer),
                },
                {
                    label: <FormattedMessage id="app.bonusSettlements.customerOverview.action.reload" />,
                    onClick: () => onReloadCustomer(customer),
                }
            );
        }

        if (customer.status === BonusSettlementCustomerStatus.Completed) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlements.customerOverview.action.reopen" />,
                // Reopening leaves the member's credit note standing; it is said
                // before, not found out afterwards (#2294).
                onClick: () => setReopenCustomer(customer),
            });
        }

        return actions;
    };

    return (
        <>
            <PoolDocumentTitle
                titleTranslationId="app.navigation.bonusSettlements"
                name={intl.formatMessage({ id: 'app.bonusSettlements.tab.customer' })}
            />
            <PaloListView<BonusSettlementCustomer>
                embedded={true}
                columns={orderColumns(columns)}
                onMoveColumn={moveColumn}
                hiddenColumnKeys={hiddenColumnKeys}
                shownColumnKeys={shownColumnKeys}
                onToggleColumn={toggleColumn}
                items={customers?.items ?? []}
                getRowId={customer => customer.customerId}
                requesting={requesting}
                emptyLabel="Keine Mitglieder in dieser Abrechnung."
                onRowClick={openDetail}
                rowActions={rowActions}
                pagination={{
                    count: customers?.total ?? 0,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                <PaloNextThemeProvider>
                    <PoolConfirmationModal
                        open={!!completeCustomer}
                        isSuccessDialog={true}
                        title={<FormattedMessage id="app.bonusSettlements.customerOverview.action.complete" />}
                        onCancel={() => setCompleteCustomer(null)}
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onConfirm={() => onConfirmCompleteCustomer(completeCustomer!)}
                        action={<FormattedMessage id="app.common.complete" />}
                    >
                        <PoolFormattedMessage
                            id={completeCustomer ? completeTextOf(completeCustomer) : COMPLETE_TEXT}
                            values={{ customer: completeCustomer?.customerName }}
                        />
                    </PoolConfirmationModal>
                    <PoolConfirmationModal
                        open={!!reopenCustomerRow}
                        isSuccessDialog={true}
                        title={<FormattedMessage id="app.bonusSettlements.customerOverview.action.reopen" />}
                        onCancel={() => setReopenCustomer(null)}
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onConfirm={() => onReopenCustomer(reopenCustomerRow!)}
                        action={<FormattedMessage id="app.bonusSettlements.customerOverview.action.reopen" />}
                    >
                        <PoolFormattedMessage
                            id={
                                reopenCustomerRow?.documents.length
                                    ? 'app.bonusSettlements.customerOverview.reopen.text'
                                    : 'app.bonusSettlements.customerOverview.reopen.textWithoutDocument'
                            }
                            values={{
                                customer: reopenCustomerRow?.customerName,
                                documents: reopenCustomerRow ? documentLines(reopenCustomerRow) : '',
                            }}
                        />
                    </PoolConfirmationModal>
                </PaloNextThemeProvider>
            </PaloListView>
        </>
    );
};
